<template>
	<div>
		<template v-if="previewForm">
			<div class="row" v-if="!isForcedPreview">
				<div class="col-12">
					<div class="float-right mb-4">
						<b-button @click="exitPreviewMode" class="mr-2">
							<b-icon icon="pencil" width="1em" height="1em"></b-icon> {{ $t('edit') }}
						</b-button>
						<button @click="saveForm(); exitPreviewMode();" class="btn btn-primary">{{ $t('save') }}</button>
					</div>
				</div>
			</div>

			<div class="row">
				<div class="col-12">
					<template v-if="previewForm.navigation === 'sidebar'">
						<TabbedView>
							<TabbedViewTab :title="page.title" :id="pageIndex.toString()" :key="pageIndex" v-for="(page, pageIndex) in filteredPages">
								<FormGrid
									:key="pageIndex"
									:page="page"
									:viewAs="previewAs" 
									:readonly="!userCanCompletePage(page)"
									:showProgress="previewForm.showProgress"
								></FormGrid>
							</TabbedViewTab>
						</TabbedView>
					</template>
					<template v-else>
						<template v-for="(page, pageIndex) in filteredPages">
							<template v-if="pageNum === (pageIndex + 1) || previewForm.navigation === 'singlepage'">
								<FormGrid
									:key="pageIndex"
									:page="page"
									:viewAs="previewAs" 
									:readonly="!userCanCompletePage(page)"
									:showProgress="previewForm.showProgress"
								></FormGrid>
							</template>
						</template>
						<div class="clearfix mb-3">
							<button class="btn btn-primary float-right ml-2" v-if="showNextPageBtn" @click="nextPage">{{ $t('next-page') }}</button>
							<button class="btn btn-secondary" v-if="showPreviousPageBtn" @click="previousPage">{{ $t('previous-page') }}</button>
						</div>
					</template>
				</div>
			</div>
		</template>
		<template v-else>
			<template v-if="hasWarnings">
				<div class="formeditor__alert">
					<button class="btn btn-warning" @click="toggleWarnings">
						<b-icon icon="exclamation-triangle-fill"></b-icon>
					</button>
				</div>
				<div class="formeditor__notices" :class="{display: showwarnings}">
					<div class="list-group">
						<div class="list-group-item list-group-item-warning">
							{{ $t('warning') }}
						</div>
						<div class="list-group-item" v-for="(error, key) in duplicateErrors" :key="key">
							{{ $t('{n}-fields-share-the-id-colon-{i}', { n: error.length, i: error[0].id }) }}
							<button class="btn btn-light mr-1 mb-1" v-for="(field, index) in error" @click="toggleEditing(field)" :key="index">
								{{ field.question }}
							</button>
						</div>
					</div>
				</div>
			</template>

			<div class="formeditor__sidebar" :class="{ 'd-none': !editing }">
				<div>
					<p>
						<strong>{{ $t('editing-field') }}</strong>
						<a class="btn btn-light btn-xs float-right" style="margin: -5px 0 0 0" @click="removeEditing()">
							<b-icon icon="x" width="1em" height="1em"></b-icon>
						</a>
					</p>
				</div>
				<FieldEditor 
                    :editing="editing" 
                    :form="settings" 
                    ref="fieldeditor" 
                    :permissions="permissions"
                    :other-fields="currentFields"
                ></FieldEditor>
				<hr />
				<button
					class="btn btn-light"
					v-if="!editing || !['grade', 'mark'].includes(editing.type)"
					@click="$refs.fieldeditor.saveFieldToLibrary(editing)"
				>
					{{ $t('save-field-to-library') }}
				</button>
				<button class="btn btn-primary float-right" @click="removeEditing">{{ $t('done') }}</button>
			</div>

			<div class="formeditor__mainwindow">
				<h2>
					{{ $t('form-editor') }}
					<div class="float-right">
						<a id="popover-target-previewas" class="btn btn-secondary mr-2" tabindex="0">
							<b-icon icon="eye" width="1em" height="1em"></b-icon> {{ $t('view-as') }}
						</a>
						<b-popover target="popover-target-previewas" triggers="focus" placement="bottom" ref="popover" custom-class="viewas-popover">
							<template #title>{{ $t('view-as') }}</template>
							<div class="d-flex flex-wrap justify-content-center">
								<b-button @click="enterPreviewModeAs('submitter')" class="mr-1 ml-1 mb-1">{{ $t('submitter') }}</b-button>
								<b-button @click="enterPreviewModeAs('reviewer')" class="mr-1 mb-1">{{ $t('reviewer') }}</b-button>
								<b-button @click="enterPreviewModeAs('signer')" class="mr-1 mb-1">{{ $t('signer') }}</b-button>
								<b-button @click="enterPreviewModeAs('subject')" class="mr-1 mb-1">{{ $t('subject') }}</b-button>
							</div>
						</b-popover>
						<button @click="saveForm" class="btn btn-primary mr-2">{{ $t('save') }}</button>
						<button @click="copyConfirm" class="btn btn-secondary">{{ $t('copy') }}</button>
					</div>
				</h2>

				<div v-if="saved">
					<div class="alert alert-success mt-8">{{ $t('form-saved') }}</div>
				</div>

				<div class="form-group">
					<label for="form-title">{{ $t('title') }}</label>
					<input id="form-title" name="formtitle" class="form-control" :class="{ error: errors.formtitle }" v-model="settings.title" placeholder="Title" required v-validate>
					<span class="text-error">{{ errors.formtitle }}</span>
				</div>

				<template>
					<div>
						<div class="card mb-3">
							<div class="card-header" @click="toggleWorkflows" style="cursor: pointer;">
								<label>{{ $t('workflows') }}</label>
								<a class="btn btn-light pull-right btn-sm" style="margin-top: -3px;">
								<b-icon :icon="`caret-${showWorkflows ? 'up' : 'down'}-fill`" width="1em" height="1em" />
								</a>
							</div>
							<div class="card-body" v-show="showWorkflows">
								<div v-for="workflow in workflows" :key="workflow.workflow_id" class="card mb-3">
								<div class="card-header">
									{{ workflow.workflow_title }}
								</div>
								<div class="card-body">
									<b-breadcrumb class="mb-0">
									<b-breadcrumb-item
										v-for="stage in workflow.stages"
										:key="stage.workflowstage_id"
										:to="`/settings/workflows/${workflow.workflow_id}/stages/${stage.workflowstage_id}`"
										:active="false"
									>
										{{ stage.workflowstage_title }}
									</b-breadcrumb-item>
									</b-breadcrumb>
								</div>
								</div>
							</div>
						</div>
					</div>
				</template>

				<div class="card mb-3">
					<div class="card-header">
						<label style="cursor:pointer;" @click="toggleSettings">{{ $t('settings') }}</label>
						<a class="btn btn-light pull-right btn-sm" data-toggle="tooltip" data-placement="top" title="Toggle Page" style="margin-top: -3px;" @click="toggleSettings">
							<b-icon :icon="`caret-${showsettings ? 'up' : 'down'}-fill`" width="1em" height="1em"></b-icon>
						</a>
					</div>
					<div class="card-body" v-if="showsettings">
						<div class="row">
							<div class="col-md-3">
								<div class="form-group">
									<label class="block">{{ $t('buttons') }}</label>
									<div class="btn-group block">
										<a :class="{ btn: true, 'btn-light': !settings.next, 'btn-success': settings.next }" @click="settings.next = !settings.next">{{ $t('next') }}</a>
										<a :class="{ btn: true, 'btn-light': !settings.previous, 'btn-success': settings.previous }" @click="settings.previous = !settings.previous">{{ $t('previous') }}</a>
									</div>
								</div>
							</div>
							<div class="col-md-3">
								<div class="form-group">
									<label class="block">{{ $t('navigation') }}</label>
									<div class="btn-group block">
										<a :class="{ btn: true, 'btn-light': settings.navigation !== 'none', 'btn-success': settings.navigation === 'none' }" @click="settings.navigation = 'none'">{{ $t('none') }}</a>
										<a :class="{ btn: true, 'btn-light': settings.navigation !== 'sidebar', 'btn-success': settings.navigation === 'sidebar' }" @click="settings.navigation = 'sidebar'">{{ $t('sidebar') }}</a>
										<a :class="{ btn: true, 'btn-light': settings.navigation !== 'singlepage', 'btn-success': settings.navigation === 'singlepage' }" @click="settings.navigation = 'singlepage'">{{ $t('single-page') }}</a>
									</div>
								</div>
							</div>
							<div class="col-md-3">
								<div class="form-group">
									<label class="block">{{ $t('show-progress') }}</label>
									<div class="btn-group block">
										<a :class="{ btn: true, 'btn-light': settings.showProgress == true, 'btn-success': settings.showProgress != true }" @click="settings.showProgress = false">{{ $t('no') }}</a>
										<a :class="{ btn: true, 'btn-light': settings.showProgress != true, 'btn-success': settings.showProgress == true }" @click="settings.showProgress = true">{{ $t('yes') }}</a>
									</div>
								</div>
							</div>
							<div class="col-md-3">
								<div class="form-group">
									<label class="block">{{ $t('is-template') }}</label>
									<div class="btn-group block">
										<a :class="{ btn: true, 'btn-light': settings.isTemplate == true, 'btn-success': settings.isTemplate != true }" @click="settings.isTemplate = false">{{ $t('no') }}</a>
										<a :class="{ btn: true, 'btn-light': settings.isTemplate != true, 'btn-success': settings.isTemplate == true }" @click="settings.isTemplate = true">{{ $t('yes') }}</a>
									</div>
								</div>
							</div>
						</div>
						<div class="row">
							<div class="col-12 col-lg-8">
								<div class="clearfix">
									<div class="pseudo-input mb-4">
										<input type="search" class="input-bare w-full flex-1" :placeholder="$t('tags')" v-if="!settings.tags.length" @click="openTagsModal" />
										<span class="badge badge-secondary badge-sm mr-2 mb-2" v-for="(tag, index) in settings.tags" :key="index">
											{{ tag.name }}
											<a @click="removeTag(tag)"><b-icon icon="x"></b-icon></a>
										</span>
										<span class="badge badge-primary badge-sm mr-2 mb-2" v-if="settings.tags.length" @click="openTagsModal">
											<b-icon icon="plus"></b-icon> {{ $t('add') }}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<draggable v-model="settings.pages" handle=".card-header" ghost-class="vuedraggable-ghost">
					<div v-for="(page, pageIndex) in settings.pages" :key="pageIndex" class="panel panel-default">
						<div class="card mb-3" :sort="true">
							<div class="card-header">
								<label style="cursor:pointer;" @click="togglePage(page)">{{ page.title }}</label>
								<a class="btn btn-light pull-right btn-sm" data-toggle="tooltip" data-placement="top" title="Toggle Page" style="margin-top: -3px;" @click="togglePage(page)">
									<b-icon :icon="`caret-${page.show ? 'up' : 'down'}-fill`" width="1em" height="1em"></b-icon>
								</a>
								<a class="btn btn-danger btn-sm float-right" style="margin: -3px 0px 0 0" @click="removePage(pageIndex)">
									<b-icon icon="trash" width="1em" height="1em"></b-icon> {{ $t('remove') }}
								</a>
							</div>
							<div class="card-body" v-show="page.show">
								<div class="form-group">
									<label>{{ $t('page-title') }}</label>
									<input class="form-control" v-model="page.title" :placeholder="$t('page-title')">
								</div>
								<div class="form-group">
									<label>{{ $t('who-can-complete-this-page') }}</label>
									<div class="btn-group block" :value="page.permission">
										<a :class="{ btn: true, 'btn-light': page.permission.length > 0, 'btn-success': page.permission.length === 0 }" @click="{ page.permission = [] }">
											{{ $t('all') }}
										</a>
										<a v-for="(permission, pkey) in permissions" :key="pkey" :class="{ btn: true, 'btn-light': !page.permission.includes(permission.value), 'btn-success': page.permission.includes(permission.value) }" @click="page.permission = togglePermission(permission.value, page)" :value="permission.value" :page="page">
											{{ permission.text }}
										</a>
									</div>
								</div>
								<div class="form-group">
									<label>{{ $t('page-style') }}</label>
									<div class="btn-group block" :value="page.permission">
										<a :class="{ btn: true, 'btn-light': page.style && page.style != 'default', 'btn-success': !page.style || page.style == 'default' }" @click="{ page.style = 'default' }">
											{{ $t('default') }}
										</a>
										<a :class="{ btn: true, 'btn-light': page.style && page.style != 'cardperquestion', 'btn-success': page.style && page.style == 'cardperquestion' }" @click="{ page.style = 'cardperquestion' }">
											{{ $t('card-per-question') }}
										</a>
									</div>
								</div>
								<template v-if="page.permission.length">
									<div class="form-check">
										<label class="form-check-label" :for="`hideifcantcomplete-${pageIndex}`">
											<input type="checkbox" class="form-check-input" :id="`hideifcantcomplete-${pageIndex}`" v-model="page.hideifcantcomplete">
											{{ $t('hide-from-other-users') }}
										</label>
									</div>
									<div class="form-group" v-if="page.hideifcantcomplete">
										<div class="btn-group block">
											<a class="btn" :class="{ 'btn-light': page.showaftersignoff || page.showafterreview, 'btn-success': !(page.showaftersignoff && page.showafterreview) }" @click="{ page.showaftersignoff = false; page.showafterreview = false }">
												{{ $t('always') }}
											</a>
											<a class="btn" :class="{ 'btn-light': !page.showafterreview, 'btn-success': page.showafterreview }" @click="{ page.showafterreview = !page.showafterreview; page.showaftersignoff = true }">
												{{ $t('until-reviewed') }}
											</a>
											<a class="btn" :class="{ 'btn-light': !page.showaftersignoff, 'btn-success': page.showaftersignoff }" @click="page.showaftersignoff = !page.showaftersignoff">
												{{ $t('until-signed-off') }}
											</a>
										</div>
									</div>
								</template>
								<div class="form-group mt-4">
									<label>{{ $t('page-fields') }}</label>
								</div>
								<div v-for="(row, rowIndex) in page.grid" :key="rowIndex" class="row">
									<div v-for="(col, colIndex) in row" :key="colIndex" :class="getColClass(col)" style="border: 1px dashed #EEE; padding: 10px;">
										<draggable v-model="col.fields" handle=".move" ghost-class="vuedraggable-ghost" :sort="true">
											<div v-for="(field, fieldIndex) in col.fields" :key="fieldIndex">
												<input type="hidden" name="field_id" :value="field.id" />
												<div style="position: relative; min-height: 50px;">
													<div style="position: absolute; top: 0; right: 0; z-index: 3;">
														<a class="btn btn-light btn-xs move" style="margin: 0" data-toggle="tooltip" data-placement="top" :title="$t('drag-to-reorder')" @click="showMoveFieldModal(pageIndex, fieldIndex)">
															<b-icon icon="arrows-move" width="1em" height="1em"></b-icon>
														</a>
														<a class="btn btn-light btn-xs ml-1" style="margin: 0" data-toggle="tooltip" data-placement="top" :title="$t('edit-field')" @click="toggleEditing(field)">
															<b-icon icon="pencil" width="1em" height="1em"></b-icon>
														</a>
														<a class="btn btn-light btn-xs ml-1" style="margin: 0" data-toggle="tooltip" data-placement="top" :title="$t('delete-field')" @click="removeField(col, fieldIndex)">
															<b-icon icon="trash" width="1em" height="1em"></b-icon>
														</a>
													</div>
													<div style="padding-right: 100px">
														<FormField :field="field" :type="field.type" :editing="true"></FormField>
													</div>
												</div>
											</div>
											<div style="text-align: right; margin-bottom: 20px;" slot="footer">
												<button class="btn btn-light btn-sm" @click="addField(col.fields)">{{ $t('add-field') }}</button>
											</div>
										</draggable>
									</div>
								</div>
							</div>
						</div>
					</div>
				</draggable>
				<div class="mb-2">
					<a id="popover-target-addpage" class="btn btn-sm btn-primary float-right" tabindex="0">{{ $t('add-page') }}</a>
					<b-popover target="popover-target-addpage" triggers="focus" placement="top" ref="popover">
						<template #title>{{ $t('add-page') }}</template>
						<b-button @click="addPage" class="mr-1">{{ $t('blank-page') }}</b-button>
						<b-button @click="copyLastPage" class="mr-1" v-if="settings.pages.length">{{ $t('copy-last-page') }}</b-button>
					</b-popover>
					<div class="clearfix"></div>
				</div>
			</div>
		</template>
	</div>
</template>


<script>
import FormField from "src/components/FormField";
import draggable from 'vuedraggable';
import { ValidateMixin } from '@/mixins';

import TabbedView from "src/components/TabbedView";
import TabbedViewTab from "src/components/TabbedViewTab";
import FormGrid from "src/components/FormEditor/FormGrid";
import FieldEditor from "src/components/FormEditor/FieldEditor";

// JSON.parse(JSON.stringify(...)) drops object keys whose value is undefined
// (e.g. an untouched select/grade field's `value`), which then stops Vue 2
// from reacting to those keys being set later. This clone preserves them.
function deepCloneWithUndefined(value) {
	if (value === null || typeof value !== 'object') {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map(deepCloneWithUndefined);
	}
	const clone = {};
	for (const key of Object.keys(value)) {
		clone[key] = deepCloneWithUndefined(value[key]);
	}
	return clone;
}

export default {

	components: {
		//VueEditor,
		FormField,
		//BtnOnClassPicker,
		draggable,
		TabbedView,
		TabbedViewTab,
		FormGrid,
		FieldEditor
	},
	mixins: [ValidateMixin],
    data: function() {
        return {
			saved: false,
			showsettings: false,
			showwarnings: false,
			editing: null,
			addToLibraryModal: false,
			addingToLibraryField: null,
			createLibraryGroupModal: false,
			libraryGroupTitle: '',
			libraryModal: false,
			libraryModalpageIndex: null,
			moveFieldModal: false,
			moveFieldpageIndex: null,
			moveFieldFieldIndex: null,
			showLibraryFields: null,
			moveLibraryFieldModal: false,
			moveLibraryFieldFieldIndex: null,
			moveLibraryFieldGroupIndex: null,
			optionTypes: {},
			workflows: [],
			showWorkflows: false,
			permissions: [
				/*{
					value: '',
					text: this.$t('all')
				},*/
				{
					value: 'submitter',
					text: this.$t('submitter')
				},
				{
					value: 'reviewer',
					text: this.$t('reviewer')
				},
				{
					value: 'signer',
					text: this.$t('signer')
				},
				{
					value: 'subject',
					text: this.$t('subject')
				}
			],
			columnSizes: [
				{
					value: 12,
					text: 'Full'
				},
				{
					value: 6,
					text: '1 / 2'
				},
				{
					value: 4,
					text: '1 / 3'
				},
				{
					value: 3,
					text: '1 / 4'
				}
			],
			form_id: null,
			settings: {
				"title": this.$t('new-form'),
				"formtype": '',
				"next": true,
				"previous": true,
				"navigation": "none",
                "showProgress": false,
				"isTemplate": false,
				"pages": [],
				"tags": []
			},
			savingToLibrary: false,
			formtypes: [],
			previewAs: null,
			pageNum: 1, // preview page num
			previewForm: null ,
        	duplicateids: [

        	],
			duplicateErrors: {},
			page: {
      			permission: [],
    		},

            statictypes: [
                "heading",
                "note_plaintext",
                "note_richtext",
                "note_html"
            ]
        }
    },
    methods: {
		togglePermission(permission,page) {
			const updatedPermission = [...page.permission];
			const index = page.permission.indexOf(permission);
			if(permission){
				if (index === -1) {
					// permission not found in the array, add it
					updatedPermission.push(permission);
				} else {
					// permission found in the array, remove it
					updatedPermission.splice(index, 1);
				}
				page.permission = updatedPermission;
				if (page.permission.length==this.permissions.length){
					// if we reach the max number of permissions, we empty the array
					page.permission=[];
				}
				//console.log(page.permission);
			} else {
				page.permission=[]
			}
			return page.permission;

  		},
    	nextPage() {
    		if(this.pageNum < this.previewForm.pages.length) {
    			this.pageNum++
    		}
    	},
    	previousPage() {
    		if(this.pageNum > 1) {
    			this.pageNum--;
    		}
    	},
    	exitPreviewMode() {
      		this.previewForm = null;
    		this.previewAs = null;
    		this.pageNum = 1;
    	},
    	enterPreviewModeAs(who) {
    		this.previewAs = who;
    		// copy form!
    		this.previewForm = deepCloneWithUndefined(this.settings);
    	},
    	getDefaultPage() {
    		return {
    			title: '',
				show: true,
				repeatable: false,
				addPageBtnText: 'Add Another Page',
				removePageBtnText: 'Remove This Page',
				grid: [
					[
						{
							"width": 12 ,
							"fields": []
						}
					]
				],
				permission: [],
				style: 'default',
				showProgress: false,
				hideifcantcomplete: false,
				showaftersignoff: false,
				showafterreview: false
			}
    	},
		patchPage(page) {
			const defaultPage = this.getDefaultPage();
			for (const [key, value] of Object.entries(defaultPage)) {
				if(!Object.hasOwnProperty.call(page, key)) {
					this.$set(page, key, value);
				}
			}
		},
    	getFormTypes() {
  			this.$cfetch(`${this.$settings.APIBaseURL}/formtypes/selectvalues`)
			.then(json => {
				if (json.status == 'SUCCESS') {
					this.formtypes= json.formtypes;
				}
			}).catch(error => {
				this.$log.error.call(this, error);
			});
    	},
    	toggleWarnings() {
    		this.showwarnings = !this.showwarnings;
    	},
    	toggleSettings() {
    		this.showsettings = !this.showsettings;
    	},
		unableToOpenForm() {
			const self = this;
			this.$store.commit("openDialog" , {
				title: this.$t('oops'),
				content: this.$t('unable-to-open-form'),
				confirmCallBack: function(){
					self.$router.replace('/formeditor/list');
				}
			});
		},
		toggleWorkflows() {
			this.showWorkflows = !this.showWorkflows;
		},
    	getForm(id, options = {}) {
  			this.$cfetch(`${this.$settings.APIBaseURL}/forms/getform/${id}`)
			.then(json => {/*The response from the server is parsed as JSON using the cfetch method.
				If the response status is "SUCCESS", the method then extracts the form object from the JSON response, and updates the form, form_id and settings properties*/
				this.$store.commit("hideBlockingSpinner");
				if (json.status == 'SUCCESS') {
					const form = json.form
					if(options.fromTemplate) {
						// Starting a new form from a template: load its settings into the UI, but
						// leave form_id unset and clear the title so saving creates a new form
						// rather than overwriting the template.
						this.form_id = null;
						form.settings.title = '';
					} else {
						this.form_id = form.id;
					}
					if(!form.settings.showProgress){
						form.settings.showProgress = false;
					}
					// Must be defaulted before settings becomes reactive below, not after - a
					// plain assignment of a key that wasn't already present on the incoming
					// object won't be picked up by Vue's reactivity once `settings` is assigned.
					if(!form.settings.isTemplate){
						form.settings.isTemplate = false;
					}
					if(options.fromTemplate) {
						// The new form is a working form, not itself a template.
						form.settings.isTemplate = false;
					}
					this.settings = form.settings;
					// backwards compatibity for old forms
					if(!this.settings.tags) {
						this.settings.tags = [];
					}
					/*Then, we iterate through each page of the form, applies a patchPage function to update any necessary properties, and then iterates through each row and column of the grid
					 on each page, applying a patchField function to each field. */
					for(let page of this.settings.pages) {
						this.patchPage(page);
						for(let row of page.grid) {
							for(let col of row) {
								for(let field of col.fields) {
									this.$refs.fieldeditor.patchField(field);
								}
							}
						}
					}
					if (json.workflows) {
                        this.workflows = json.workflows;
                    }
					this.formCheck();
					this.checkBankQuestionVersions();
					if(this.isForcedPreview) {
						this.enterPreviewModeAs(this.$route.query.viewas);
					}
				} else {
					this.unableToOpenForm();
				}
			}).catch(error => {
				this.$log.error.call(this, error);
				this.$store.commit("hideBlockingSpinner");
				this.unableToOpenForm();
				this.$log.error.call(this, error);
			});
    	},
        saveForm() {

        	this.validate();
        	if (this.hasErrors) {
        		return false;
        	}

        	const data = {
				json: true,
				save: 1,
				form_settings: this.settings
			}

			if(this.form_id) {
				data.form_id = this.form_id;
			}

 			return this.$cfetch(`${this.$settings.APIBaseURL}/forms/saveform`, {
				method: "post",
				data
			})
			.then(json => {
				if (json.status === 'SUCCESS') {
					this.form_id = json.form.id;
					this.saved = true;
					// bad ux IMHO when this doesn't go away as no feedback on subsequent saves
					setTimeout(() => {
						this.saved = false;
					}, 5000);
					return this.syncQuestionBankFields();
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			});

		},
		// Grade/mark fields are saved to the question bank automatically whenever the whole form
		// is saved - there's no separate manual step. Runs after the form itself has saved, then,
		// if any field was newly linked or moved to a new version, re-saves the form once more so
		// that linkage persists in form_settings too.
		syncQuestionBankFields() {
			const fields = this.collectQuestionBankFields();
			if(!fields.length) return Promise.resolve();

			let changed = false;
			const self = this;

			return fields.reduce((promise, field) => promise.then(() => {
				const before = field.examquestionversion_id;
				return self.$refs.fieldeditor.postQuestionBankSave(field, self.form_id, true)
					.then(() => {
						if(field.examquestionversion_id !== before) changed = true;
					});
			}), Promise.resolve())
			.then(() => {
				if(changed) {
					const data = {
						json: true,
						save: 1,
						form_id: self.form_id,
						form_settings: self.settings
					};
					return self.$cfetch(`${self.$settings.APIBaseURL}/forms/saveform`, {
						method: "post",
						data
					});
				}
			});
		},
		collectQuestionBankFields() {
			const fields = [];
			for(let page of this.settings.pages) {
				for(let row of page.grid) {
					for(let col of row) {
						for(let field of col.fields) {
							if(field && ['grade', 'mark'].includes(field.type)) {
								fields.push(field);
							}
						}
					}
				}
			}
			return fields;
		},
		copyConfirm() {
			var self = this;
			this.$store.commit("openDialog", {
				cancelBtn: true,
				title: this.$t("copy"),
				content: this.$t('save-first-q'),
				confirmBtnText: this.$t('yes'),
				cancelBtnText: this.$t('no'),
				confirmCallBack() {
					self.saveForm().then(function() {
						self.copyForm();
					});
				},
				cancelCallBack() {
					self.copyForm();
				}
			});
		},
		copyForm() {
			this.saved = false;
			this.settings.title = `${this.settings.title} ${this.$t('copy-parentheses')}`;
			this.form_id = null;
		},
		getColClass(col){
			return 'col-md-' + col.width;
		},
		removeEditing(){
			if(this.editing){
				this.$refs.fieldeditor.tryGenerateFieldId(this.editing);
				this.formCheck();
				if('id' in this.editing && !this.editing.id){
					this.$store.commit('openDialog', {
						title: this.$t('missing-id'),
						content: this.$t('field-without-id-warning'),
						confirmCallBack: () => {
							if(this.editing){
								this.$refs.fieldeditor.editIdField();
							}
						},
					});
					return false;
				} else {
                    this.editing = null;
                    return true;
                }
			}
			return true;
		},
		toggleEditing(field){
			if(this.removeEditing()){
				this.editing = field;
				this.$refs.fieldeditor.setUserWantsToEditField(false);
			}
		},
		addPage() {
			this.$refs.popover.$emit('close');
			if (this.settings.pages == undefined) this.settings.pages = [];
			this.$set(this.settings.pages, this.settings.pages.length, this.getDefaultPage());
		},
		copyLastPage() {
			this.$refs.popover.$emit('close');
			const lastPage = this.settings.pages[this.settings.pages.length - 1];
			const lastPageCopy = JSON.parse(JSON.stringify(lastPage));
			this.$set(this.settings.pages, this.settings.pages.length, lastPageCopy);
		},
		removePage(pageIndex) {
			var self = this;
			this.$store.commit("openDialog" , {
				cancelBtn: true ,
				title: this.$t('confirm'),
				content: this.$t('delete-this-form-page-question'),
				confirmCallBack: function(){
					self.$delete(self.settings.pages, pageIndex);
				}
			});
		},
		togglePage(page) {
			page.show = !page.show;
		},
		addField(fields) {
			if(this.removeEditing()){
				const self = this;
				this.$store.commit("openModal", {
					type: "selector",
					title: this.$t('add-field'),
					selectCallback: function(choice) {
						if(choice.action === 'new') {
							self.$store.commit("closeModal");
							self.$set(fields, fields.length, self.$refs.fieldeditor.getDefaultField());
							self.editing = fields[fields.length - 1];
						} else if(choice.action === 'library') {
							self.openFieldLibrarySelector(fields);
						} else if(choice.action === 'bank') {
							self.openQuestionBankSelector(fields);
						}
					},
					selected: [],
					data: [
						{ action: 'new', caption: this.$t('new-field'), subcaption: this.$t('new-field-help') },
						{ action: 'library', caption: this.$t('from-library'), subcaption: this.$t('from-library-help') },
						{ action: 'bank', caption: this.$t('from-question-bank'), subcaption: this.$t('from-question-bank-help') }
					],
					captionKey: 'caption',
					subcaptionKey: 'subcaption'
				});
			}
		},
		openFieldLibrarySelector(fields) {
			const self = this;
			const data = {
				type: 'field'
			}

			this.$cfetch(`${this.$settings.APIBaseURL}/formlibrary/list`, {
				data
			})
			.then(json => {
				if (json.status === "SUCCESS") {

					this.$store.commit("openModal", {
						type: "selector",
						title: this.$t('select-a-field'),
						selectCallback: function(selection) {
							const field = selection.formlibrary_json;
                            if (Array.isArray(field)){
                                field.forEach(field => {
                                    const clonedField = JSON.parse(JSON.stringify(field));
                                    //clonedField.id = ''; // reset ID
                                    clonedField.formlibrary_id = selection.formlibrary_id;
                                    self.$refs.fieldeditor.patchField(clonedField);
							        self.$set(fields, fields.length, clonedField);
                                })
                            } else {
                                const clonedField = JSON.parse(JSON.stringify(field));
                                //clonedField.id = '';
                                field.id = '';
                                clonedField.formlibrary_id = selection.formlibrary_id;
                                self.$refs.fieldeditor.patchField(clonedField);
                                self.$set(fields, fields.length, clonedField);
                                self.editing = fields[fields.length-1];

                            }

                            self.$store.commit("closeModal");

						},
						selected: [],
						data: json.items,
						captionKey: 'formlibrary_title',
						subcaptionKey: 'formlibrary_notes'
					});
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			});
		},
		openQuestionBankSelector(fields) {
			const self = this;
			this.$cfetch(`${this.$settings.APIBaseURL}/examquestions/list`, {
				data: { search: '' }
			})
			.then(json => {
				if (json.status === "SUCCESS") {
					const items = json.results.map(item => ({
						...item,
						question: item.currentversion ? item.currentversion.question : ''
					}));
					self.$store.commit("openModal", {
						type: "selector",
						title: self.$t('select-a-question'),
						selectCallback: function(selection) {
							self.$cfetch(`${self.$settings.APIBaseURL}/examquestions/currentversions`, {
								method: "post",
								data: { examquestion_ids: [selection.id] }
							})
							.then(versionsjson => {
								if (versionsjson.status === "SUCCESS") {
									const current = versionsjson.results[selection.id];
									const field = self.buildFieldFromBankQuestion(selection, current);
									self.$set(fields, fields.length, field);
									self.editing = fields[fields.length - 1];
								}
								self.$store.commit("closeModal");
							})
							.catch(error => {
								self.$log.error.call(self, error);
								self.$store.commit("closeModal");
							});
						},
						selected: [],
						data: items,
						captionKey: 'name',
						subcaptionKey: 'question',
						idProperty: 'id'
					});
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			});
		},
		buildFieldFromBankQuestion(question, current) {
			const type = current.examquestiontype_name === 'select' ? 'grade' : 'mark';
			const field = this.$refs.fieldeditor.getDefaultField(type);
			field.question = current.question;
			field.examquestion_id = question.id;
			field.examquestionversion_id = current.examquestionversion_id;
			field.examquestionoutdated = false;
			field.examquestioncurrentversion = current;
			field.description = current.description;
			field.info = current.info;
			if (type === 'grade') {
				field.responsevaluegroup_id = current.responsevaluegroup_id;
				field.options = (current.options || []).map(o => ({
					responsevalue_id: o.responsevalue_id,
					value: o.value,
					caption: o.caption,
					info: o.description,
					cssclass: o.cssclass,
					showinfo: false
				}));
			} else {
				field.min = current.markmin;
				field.max = current.markmax;
			}
			this.$refs.fieldeditor.tryGenerateFieldId(field);
			return field;
		},
		checkBankQuestionVersions() {
			const examquestionIds = new Set();
			const fieldsByQuestionId = {};
			for(let page of this.settings.pages) {
				for(let row of page.grid) {
					for(let col of row) {
						for(let field of col.fields) {
							if(field && field.examquestion_id) {
								examquestionIds.add(field.examquestion_id);
								if(!fieldsByQuestionId[field.examquestion_id]) fieldsByQuestionId[field.examquestion_id] = [];
								fieldsByQuestionId[field.examquestion_id].push(field);
							}
						}
					}
				}
			}
			if(!examquestionIds.size) return;
			const self = this;
			this.$cfetch(`${this.$settings.APIBaseURL}/examquestions/currentversions`, {
				method: "post",
				data: { examquestion_ids: Array.from(examquestionIds) }
			})
			.then(json => {
				if(json.status === "SUCCESS") {
					for(const [examquestionId, current] of Object.entries(json.results)) {
						for(const field of (fieldsByQuestionId[examquestionId] || [])) {
							self.$set(field, 'examquestionoutdated', field.examquestionversion_id != current.examquestionversion_id);
							self.$set(field, 'examquestioncurrentversion', current);
						}
					}
				}
			})
			.catch(error => {
				self.$log.error.call(self, error);
			});
		},
		removeField(col, fieldIndex) {
			this.removeEditing();
			var self = this;
			this.$store.commit("openDialog" , {
				cancelBtn: true ,
				title: this.$t('confirm'),
				content: this.$t('delete-this-form-field-question'),
				confirmCallBack: function(){
					self.$delete(col.fields, fieldIndex);
				}
			});
		},
		showMoveFieldModal(pageIndex, fieldIndex) {
			this.moveFieldpageIndex = pageIndex;
			this.moveFieldFieldIndex = fieldIndex;
			this.moveFieldModal = true;
		},
		closeMoveFieldModal() {
			this.moveFieldpageIndex = null;
			this.moveFieldFieldIndex = null;
			this.moveFieldModal = false;
		},
		moveField(newpageIndex) {
			let fieldIndex = this.moveFieldFieldIndex;
			let pageIndex = this.moveFieldpageIndex;
			let field = Object.assign({}, this.settings.pages[pageIndex].fields[fieldIndex]);

			this.settings.pages[pageIndex].fields.splice(fieldIndex, 1);
			this.settings.pages[newpageIndex].fields.push(field);
			this.closeMoveFieldModal();
		},
		openTagsModal() {
			const self = this
			this.$store.commit("openModal", {
				type: "selector",
				title: this.$t('select-tags'),
				selectCallback: function(selection) {
					const isSelected = self.settings.tags.find(
						(tag, i) => {
							if(tag.id === selection.id){
								self.$delete(self.settings.tags, i);
								return true;
							}
						}
					);
					if (!isSelected) {
						self.settings.tags.push(selection);
					}
				},
				selected: this.settings.tags,
				data: this.$config.getValue('tags'),
				captionKey: 'name'
			});
		},
		removeTag(selection) {
			const self = this
			self.settings.tags.find(
				(tag, i) => {
					if(tag.id === selection.id){
						self.$delete(self.settings.tags, i);
						return true;
					}
				}
			);
			return false;
		},
		userCanCompletePage(page) {
			return (!('permission' in page) ||
				!page.permission ||
				page.permission.length == 0 ||
				page.permission.includes(this.previewAs));
		},
		userCanViewPage(page) {
			return !page.hideifcantcomplete || this.userCanCompletePage(page);
		},
		checkDuplicatedIds() {
			this.settings.pages.forEach(page => {
				console.log(page);
			});
		},
		countDuplicatedFieldNames(item,entry) {
      		let count = 0;
			let fieldNames= [];
			//First, we populate the array with all the field names
      		for (let i = 0; i < (Object.keys(item).length); i++) {
				fieldNames.push(item[i]['id']);
      		}
			//Next, we  check if the entry has appeared in the fieldNames
			for (let i = 0; i < (Object.keys(item).length); i++) {
        		if (fieldNames[i] === entry) {
          		count++;
        		}
      		}
      		return count;
    	},
		formCheck() {
			//The function creates an empty object called ids. This object will be used to keep track of which field IDs have been encountered.
      		var ids ={ };
			//The function creates an empty object called duplicateErrors. This object will be used to store information about any fields with duplicate IDs.
      		this.duplicateErrors =  {};
			/*The function loops through each page in the form's settings, then each row in the page's grid, and then each field in the row's columns.
			This nested loop structure allows the function to examine each field in the form.*/
   			for(let page of this.settings.pages) {
    			for(let row of page.grid) {
     				for(let col of row) {
      					for(let field of col.fields) {
							/*For each field, the function checks to see if the field has an id property. */
       						if('id' in field){
								/*If it does, the function checks to see if that ID has already been encountered.*/
        						if(!(field.id in ids)){
									/*If it hasn't, the function adds the ID to the ids object and sets its value to an empty array.*/
         							ids[field.id] = [];
        						}
								/*If the ID has already been encountered, the function adds the field to the array of fields associated with that ID,
								and adds an entry to the duplicateErrors object with the ID as the key and the array of fields as the value.*/
        						//ids[field.id].push(field);
								ids[field.id].push(field);
        						if(ids[field.id].length > 1){
         							this.duplicateErrors[field.id] = ids[field.id];
        						}
       						}
      					}
     				}
    			}
			}
     	},
        cloneField(field) {
            if (field == null || typeof field !== 'object') {
                return field;
            }
            if (Array.isArray(field)) {
                return field.map(item => this.cloneField(item));
            }
            const clone = {};
            for (const key in field) {
                if (Object.prototype.hasOwnProperty.call(field, key)) {
                clone[key] = this.cloneField(field[key]);
                }
            }
            return clone;
        }
    },
    computed: {
    	isForcedPreview() {
    		return !!this.$route.query.viewas;
    	},
    	filteredPages() {
    		const pages = [];
    		if(this.previewForm) {
    			for(let page of this.previewForm.pages) {
    				if(this.userCanViewPage(page)) {
						//console.log(this.userCanViewPage(page));
    					pages.push(page);
    				}
    			}
    		}
			//console.log(pages);
    		return pages;
    	},
    	showNextPageBtn() {
    		return this.previewForm && this.previewForm.next && this.pageNum < this.filteredPages.length;
    	},
    	showPreviousPageBtn() {
    		return this.previewForm && this.previewForm.previous && this.pageNum > 1;
    	},
		hasWarnings() {
    		return Object.keys(this.duplicateErrors).length != 0;
  		},
        currentFields(){
            if (!this.settings.pages) return undefined;
            let fields = [];
            for (const page of this.settings.pages) {
                for (const row of page.grid) {
                    for (const col of row) {
                        for (const field of col.fields) {
                            if (typeof field === 'object' && field !== null && !Array.isArray(field) && !this.statictypes.includes(field.type)) {
                                fields.push(field)
                            }
                        }
                    }
                }
            }
            return fields
        }
        
   	},
    mounted() {
    	if(this.$route.params.formid) {
    		this.getForm(this.$route.params.formid);
    	} else if(this.$route.query.fromtemplate) {
    		this.getForm(this.$route.query.fromtemplate, { fromTemplate: true });
    	} else {
    		this.$store.commit("hideBlockingSpinner");
    		this.addPage();
    	}
    	this.getFormTypes();

    }
}
</script>
<style>
.viewas-popover {
	max-width: 320px;
}
</style>
